import React, { useState, useEffect, useRef } from 'react';
import DocumentFolderContent from './DocumentFolderContent';
import authAxios from "../../services/Token"

const DocumentManagement = () => {
    const [folders, setFolders] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [newFolderName, setNewFolderName] = useState('');
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingFolderContent, setLoadingFolderContent] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        const idUsuario = localStorage.getItem("idusuario");
        if (isMounted.current) {
            setUserId(idUsuario);
        }
        loadFolders();
        return () => {
            isMounted.current = false;
        };
    }, []);

    const loadFolders = async () => {
        if (!isMounted.current) return;
        
        setLoading(true);
        try {
            const response = await authAxios.get('folders');
            if (isMounted.current) {
                setFolders(response.data);
            }
        } catch (error) {
            console.error('Error loading folders:', error);
            if (isMounted.current) {
                alert('Error al cargar las carpetas: ' + (error.response?.data?.message || error.message));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const loadFolderContent = async (folderId) => {
        if (!isMounted.current) return;
        
        setLoadingFolderContent(true);
        try {
            const response = await authAxios.get(`folders/${folderId}`);
            if (isMounted.current) {
                setCurrentFolder(response.data);
            }
        } catch (error) {
            console.error('Error loading folder content:', error);
            if (isMounted.current) {
                alert('Error al cargar el contenido de la carpeta');
            }
        } finally {
            if (isMounted.current) {
                setLoadingFolderContent(false);
            }
        }
    };

    const handleFolderClick = async (folder) => {
        await loadFolderContent(folder.id);
    };

    const toggleFolderExpansion = (folderId, event) => {
        if (event) {
            event.stopPropagation(); // Prevenir que se active el click de la carpeta
        }
        
        setExpandedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    };

    const createFolder = async (parentId = null) => {
        if (!newFolderName.trim()) {
            alert('Por favor ingresa un nombre para la carpeta');
            return;
        }

        if (!userId) {
            alert('No se encontró el ID de usuario');
            return;
        }

        try {
            await authAxios.post('folders', {
                name: newFolderName,
                parent_id: parentId
            });
            
            if (isMounted.current) {
                setNewFolderName('');
                loadFolders();
                if (currentFolder && parentId === currentFolder.id) {
                    loadFolderContent(parentId);
                }
                alert('Carpeta creada correctamente');
            }
        } catch (error) {
            console.error('Error creating folder:', error);
            if (isMounted.current) {
                alert('Error al crear la carpeta: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    // Función para filtrar carpetas recursivamente
    const filterFolders = (folderList, term) => {
        if (!folderList || folderList.length === 0) return [];

        return folderList.filter(folder => {
            const matchesName = folder.name.toLowerCase().includes(term.toLowerCase());
            const childrenMatch = folder.children ? filterFolders(folder.children, term).length > 0 : false;
            
            return matchesName || childrenMatch;
        }).map(folder => ({
            ...folder,
            children: folder.children ? filterFolders(folder.children, term) : []
        }));
    };

    // Función recursiva para expandir todas las carpetas que contengan el término de búsqueda
    const expandMatchingFolders = (folderList, term, parentIds = []) => {
        if (!folderList || folderList.length === 0) return;

        folderList.forEach(folder => {
            const matchesName = folder.name.toLowerCase().includes(term.toLowerCase());
            const hasMatchingChildren = folder.children ? 
                filterFolders(folder.children, term).length > 0 : false;

            if (matchesName || hasMatchingChildren) {
                // Expandir esta carpeta y todas sus padres
                const allIdsToExpand = [...parentIds, folder.id];
                allIdsToExpand.forEach(id => {
                    setExpandedFolders(prev => new Set([...prev, id]));
                });
            }

            // Continuar recursivamente con los hijos
            if (folder.children) {
                expandMatchingFolders(folder.children, term, [...parentIds, folder.id]);
            }
        });
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        
        if (term.trim() === '') {
            // Si la búsqueda está vacía, colapsar todas las carpetas
            setExpandedFolders(new Set());
        } else {
            // Expandir carpetas que coincidan con la búsqueda
            expandMatchingFolders(folders, term);
        }
    };

    // Función recursiva para renderizar carpetas con colapsado/expandido
    const renderFolderTree = (folderList, level = 0, parentIds = []) => {
        if (!folderList || folderList.length === 0) return null;

        return folderList.map(folder => {
            const hasChildren = folder.children && folder.children.length > 0;
            const isExpanded = expandedFolders.has(folder.id);
            const currentPath = [...parentIds, folder.id];

            return (
                <div key={folder.id} style={{ marginLeft: level * 15 }}>
                    <div 
                        className={`folder-item p-2 border rounded mb-1 ${currentFolder?.id === folder.id ? 'bg-primary text-white' : 'bg-light text-black'}`}
                        onClick={() => handleFolderClick(folder)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1 d-flex align-items-center">
                                {/* Icono de expandir/colapsar */}
                                {hasChildren && (
                                    <span 
                                        className="me-2"
                                        onClick={(e) => toggleFolderExpansion(folder.id, e)}
                                        style={{ 
                                            cursor: 'pointer',
                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s',
                                            display: 'inline-block'
                                        }}
                                        title={isExpanded ? 'Colapsar' : 'Expandir'}
                                    >
                                        ▶
                                    </span>
                                )}
                                {!hasChildren && (
                                    <span className="me-2" style={{ width: '16px', display: 'inline-block' }}></span>
                                )}
                                
                                <span className="me-2">
                                    {hasChildren ? (isExpanded ? '📂' : '📁') : '📁'}
                                </span>
                                <strong>{folder.name}</strong> 
                                <small className={currentFolder?.id === folder.id ? 'text-light' : 'text-muted'} style={{ marginLeft: '8px' }}>
                                    (Nivel {folder.level})
                                </small>
                            </div>
                            <div>
                                {hasChildren && (
                                    <span className="badge bg-secondary me-2">
                                        {folder.children.length}
                                    </span>
                                )}
                                {folder.documents && folder.documents.length > 0 && (
                                    <span className="badge bg-info">
                                        {folder.documents.length}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Renderizar hijos si existen, está expandido y no hemos alcanzado el nivel máximo */}
                    {hasChildren && isExpanded && level < 6 && (
                        <div>
                            {renderFolderTree(folder.children, level + 1, currentPath)}
                        </div>
                    )}
                </div>
            );
        });
    };

    const filteredFolders = searchTerm.trim() === '' ? folders : filterFolders(folders, searchTerm);

    if (!isMounted.current) {
        return null;
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-md-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3>Estructura de Carpetas</h3>
                    </div>

                    {/* Filtro de búsqueda */}
                    <div className="mb-3">
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar carpeta por nombre..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            {searchTerm && (
                                <button 
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setExpandedFolders(new Set());
                                    }}
                                    title="Limpiar búsqueda"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        {searchTerm && (
                            <small className="text-muted">
                                {filteredFolders.length} carpeta(s) encontrada(s)
                            </small>
                        )}
                    </div>

                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre de nueva carpeta principal"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && createFolder()}
                        />
                        <button 
                            className="btn btn-primary mt-2"
                            onClick={() => createFolder()}
                            disabled={!newFolderName.trim() || !userId || loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Creando...
                                </>
                            ) : (
                                'Crear Carpeta Principal'
                            )}
                        </button>
                        {!userId && (
                            <div className="alert alert-warning mt-2">
                                No se encontró el ID de usuario en el localStorage
                            </div>
                        )}
                    </div>
                    
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                        {loading ? (
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                                <p className="mt-2">Cargando carpetas...</p>
                            </div>
                        ) : filteredFolders.length > 0 ? (
                            renderFolderTree(filteredFolders)
                        ) : searchTerm ? (
                            <div className="text-center text-muted py-4">
                                <p>No se encontraron carpetas que coincidan con "{searchTerm}"</p>
                                <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setExpandedFolders(new Set());
                                    }}
                                >
                                    Mostrar todas las carpetas
                                </button>
                            </div>
                        ) : (
                            <p className="text-muted">No hay carpetas creadas</p>
                        )}
                    </div>

                    {/* Controles de expandir/colapsar */}
                    <div className="mt-3 d-flex gap-2">
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                // Expandir todas las carpetas
                                const expandAll = (folderList, ids = []) => {
                                    folderList.forEach(folder => {
                                        ids.push(folder.id);
                                        if (folder.children) {
                                            expandAll(folder.children, ids);
                                        }
                                    });
                                    return ids;
                                };
                                setExpandedFolders(new Set(expandAll(folders)));
                            }}
                        >
                            Expandir Todo
                        </button>
                        <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setExpandedFolders(new Set())}
                        >
                            Colapsar Todo
                        </button>
                    </div>
                </div>
                
                <div className="col-md-8">
                    {loadingFolderContent ? (
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-2">Cargando contenido de la carpeta...</p>
                        </div>
                    ) : currentFolder ? (
                        <DocumentFolderContent 
                            folder={currentFolder} 
                            onUpdate={() => {
                                loadFolderContent(currentFolder.id);
                                loadFolders();
                            }}
                            userId={userId}
                        />
                    ) : (
                        <div className="alert alert-info text-center py-4">
                            <div className="fs-1">📁</div>
                            <h4>Selecciona una carpeta para gestionar documentos</h4>
                            <p className="mb-0">Haz clic en cualquier carpeta del panel izquierdo para ver y gestionar sus documentos</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentManagement;